import React from 'react';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Trophy, Users, Megaphone } from 'lucide-react';

/**
 * Homepage Mindset Quiz promo section.
 * Now surfaces three demographic-specific quiz paths (Athlete / Parent /
 * Coach) so visitors immediately see there's a version built for THEM. Each
 * audience card deep-links to `/quiz?audience=...` which skips the audience
 * picker step on the quiz itself.
 */
const AUDIENCE_CARDS = [
  {
    key: 'athlete',
    label: "I'm the athlete",
    Icon: Trophy,
    pitch: 'Diagnose your mental game. 4 archetypes. 8 questions.',
    cta: 'Take the Athlete Quiz',
    href: '/quiz?audience=athlete',
  },
  {
    key: 'parent',
    label: "I'm a parent",
    Icon: Users,
    pitch: 'Find out how you actually show up after a tough game.',
    cta: 'Take the Parent Quiz',
    href: '/quiz?audience=parent',
  },
  {
    key: 'coach',
    label: "I'm a coach",
    Icon: Megaphone,
    pitch: "Diagnose your coaching style — and where to grow.",
    cta: 'Take the Coach Quiz',
    href: '/quiz?audience=coach',
  },
];

export const MindsetQuizPromo = () => {
  return (
    <section
      id="quiz"
      className="relative py-16 sm:py-20 bg-gradient-to-br from-[#004C54] to-[#003a40] text-white overflow-hidden"
      data-testid="quiz-promo-section"
    >
      {/* Decorative accents */}
      <div
        className="absolute top-0 right-0 w-[280px] h-[280px] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: '#4FBCC7' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#4FBCC7' }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1100px] mx-auto px-5 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-[640px] mx-auto mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-[11px] font-mono-accent font-bold uppercase tracking-[0.18em] text-[#4FBCC7] mb-4">
            <Sparkles className="h-3 w-3" /> Free · 2 minutes · No signup
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4">
            Three quizzes.<br className="hidden sm:block" /> Three different answers.
          </h2>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed">
            We built a separate Mindset Quiz for each role in an athlete's world.
            Eight questions. One result. The exact framework to run first — whether you play
            the game, support someone who does, or coach the team.
          </p>
        </div>

        {/* Audience cards */}
        <div
          className="grid sm:grid-cols-3 gap-4 sm:gap-5"
          data-testid="quiz-promo-audience-cards"
        >
          {AUDIENCE_CARDS.map(({ key, label, Icon, pitch, cta, href }) => (
            <div
              key={key}
              className="rounded-2xl bg-white/8 backdrop-blur border border-white/12 p-5 sm:p-6 flex flex-col hover:bg-white/12 hover:border-white/20 transition-colors"
              data-testid={`quiz-promo-card-${key}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#4FBCC7]/20 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-[#4FBCC7]" />
              </div>
              <div className="font-heading text-xl font-bold mb-1.5">{label}</div>
              <p className="text-sm text-white/70 leading-snug mb-5 flex-1">
                {pitch}
              </p>
              <Button
                asChild
                size="sm"
                className="bg-white text-[#004C54] hover:bg-white/90 font-semibold text-[13px] h-10 px-4 rounded-md w-full active:scale-[0.98]"
              >
                <a href={href} data-testid={`quiz-promo-cta-${key}`}>
                  {cta} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </a>
              </Button>
            </div>
          ))}
        </div>

        {/* Sub-line: what each path includes */}
        <p className="text-center text-[12.5px] text-white/55 mt-7 max-w-[680px] mx-auto leading-relaxed">
          Each quiz comes with a free PDF playbook tailored to your role — the Apex Six frameworks
          for athletes, the Car Ride Home Playbook for parents, and the Coach's Reset for coaches.
        </p>
      </div>
    </section>
  );
};

export default MindsetQuizPromo;
